import React from 'react';
import { Heart, Activity, Percent, User, Info } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';

/**
 * Компонент отображения результатов расчета целевых зон пульса
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  return (
    <ToolResult title="Результаты расчета целевых зон пульса">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Максимальный пульс */}
        <div className="rounded-lg bg-gradient-to-br from-red-50 to-pink-50 dark:from-red-900/30 dark:to-pink-900/30 p-5 border border-red-100 dark:border-red-800/50 shadow-sm">
          <h3 className="text-base font-medium text-red-700 dark:text-red-300 mb-1.5 flex items-center gap-1.5">
            <Heart className="w-4 h-4"/>
            Максимальный пульс
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-red-800 dark:text-red-200" aria-live="polite">
              {results.maxHeartRate} уд/мин
            </span>
          </div>
          <p className="text-xs text-red-600 dark:text-red-400 mt-1">
            Расчет по формуле: {results.maxHRFormula}
          </p>
        </div>

        {/* Целевая зона */}
        <div className="rounded-lg bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/30 dark:to-indigo-900/30 p-5 border border-blue-100 dark:border-blue-800/50 shadow-sm">
          <h3 className="text-base font-medium text-blue-700 dark:text-blue-300 mb-1.5 flex items-center gap-1.5">
            <Activity className="w-4 h-4"/>
            Целевая зона пульса
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-blue-800 dark:text-blue-200" aria-live="polite">
              {results.targetZone.lowerHR} - {results.targetZone.upperHR} уд/мин
            </span>
          </div>
          <p className="text-xs text-blue-600 dark:text-blue-400 mt-1">
            {results.targetZone.lowerPercent}% - {results.targetZone.upperPercent}% от максимального пульса
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Тип тренировки" 
          value={results.exerciseType} 
          color="blue" 
          icon={<Activity className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Метод расчета" 
          value={results.maxHRFormula} 
          icon={<Info className="w-4 h-4"/>} 
        />
        
        <ResultItem 
          label="Максимальный пульс" 
          value={`${results.maxHeartRate} уд/мин`} 
          color="red" 
          icon={<Heart className="w-4 h-4"/>} 
        />
        
        {results.restingHeartRate && (
          <ResultItem 
            label="Пульс в покое" 
            value={`${results.restingHeartRate} уд/мин`} 
            color="green" 
            icon={<Heart className="w-4 h-4"/>} 
          />
        )}

        <ResultItem 
          label="Физическая подготовка" 
          value={results.fitnessLevel} 
          icon={<User className="w-4 h-4"/>} 
        />
        
        {results.healthCondition !== 'Здоров(а)' && (
          <ResultItem 
            label="Состояние здоровья" 
            value={results.healthCondition} 
            color="yellow" 
          />
        )}
        
        <ResultItem 
          label="Формула расчета целевых зон" 
          value={results.useKarvonen ? results.karvonenFormula : results.directFormula} 
          color="indigo" 
          icon={<Percent className="w-4 h-4"/>} 
        />

        {/* Базовые параметры */}
        <div className="flex flex-wrap gap-3 mt-1">
          <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-full text-xs text-gray-700 dark:text-gray-300">
            <span className="font-medium">Возраст:</span> {results.age} лет
          </div>
          <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-full text-xs text-gray-700 dark:text-gray-300">
            <span className="font-medium">Пол:</span> {results.gender === 'male' ? 'мужской' : 'женский'}
          </div>
          {results.genderAdjustment !== 0 && (
            <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-full text-xs text-gray-700 dark:text-gray-300">
              <span className="font-medium">Коррекция по полу:</span> {results.genderAdjustment > 0 ? '+' : ''}{results.genderAdjustment}%
            </div>
          )}
          {results.fitnessAdjustment !== 0 && (
            <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-full text-xs text-gray-700 dark:text-gray-300">
              <span className="font-medium">Коррекция по физ. подготовке:</span> {results.fitnessAdjustment > 0 ? '+' : ''}{results.fitnessAdjustment}%
            </div>
          )}
          {results.healthAdjustment !== 0 && (
            <div className="px-3 py-1.5 bg-gray-100 dark:bg-gray-700 rounded-full text-xs text-gray-700 dark:text-gray-300">
              <span className="font-medium">Коррекция по здоровью:</span> {results.healthAdjustment > 0 ? '+' : ''}{results.healthAdjustment}%
            </div>
          )}
        </div>

        {/* Рекомендации */}
        <div className="bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 mt-2 border border-gray-200 dark:border-gray-700">
          <div className="flex items-start gap-3">
            <div className="mt-0.5">
              <Activity className="w-5 h-5 text-green-500" />
            </div>
            <div>
              <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Рекомендации по тренировкам</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Для достижения наилучших результатов в {results.exerciseType.toLowerCase()}, поддерживайте пульс в диапазоне <span className="font-medium text-blue-600 dark:text-blue-400">{results.targetZone.lowerHR} - {results.targetZone.upperHR} уд/мин</span>. 
                {results.healthCondition !== 'Здоров(а)' && ' При вашем состоянии здоровья рекомендуется начинать с нижней границы диапазона и медленно увеличивать нагрузку по согласованию с врачом.'}
              </p>
              
              {results.healthCondition === 'Здоров(а)' && (
                <p className="text-sm text-gray-600 dark:text-gray-400 mt-2">
                  Контролируйте пульс во время тренировок с помощью фитнес-трекера или пульсометра. Для эффективных результатов рекомендуется заниматься в целевой зоне пульса не менее 20-30 минут 3-5 раз в неделю.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    </ToolResult>
  );
};

export default ResultsOverview;